import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AssignmentIcon from "@material-ui/icons/Assignment";
import moment from "moment";
import DnerhsApi from "../api/DnerhsApi";
import Alert from "../components/Alert";
import { trackPromise } from "react-promise-tracker";

const TIPOS_RECTIFICACION = [
  { value: "BAJA", label: "Baja", description: "Requiere fecha desde y hasta" },
  { value: "ALTA", label: "Alta", description: "Requiere fecha desde y hasta" },
  { value: "OTROS", label: "Otros", description: "Solo observacion" },
];

const parseDateOnly = (value) => {
  if (!value) {
    return "";
  }

  if (moment.isMoment(value)) {
    return value.format("YYYY-MM-DD");
  }

  if (value instanceof Date) {
    return moment([value.getFullYear(), value.getMonth(), value.getDate()]).format("YYYY-MM-DD");
  }

  const textValue = String(value);
  const isoDateMatch = textValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateMatch) {
    return `${isoDateMatch[1]}-${isoDateMatch[2]}-${isoDateMatch[3]}`;
  }

  const localDateMatch = textValue.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (localDateMatch) {
    return `${localDateMatch[3]}-${localDateMatch[2]}-${localDateMatch[1]}`;
  }

  const parsedDate = moment(textValue);
  return parsedDate.isValid() ? parsedDate.format("YYYY-MM-DD") : "";
};

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: 620,
    maxWidth: "100vw",
    backgroundColor: "#fafafa",
    [theme.breakpoints.down("xs")]: {
      width: "100vw",
    },
  },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e0e0e0",
    padding: theme.spacing(2, 3),
  },
  content: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(12),
  },
  footer: {
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    backgroundColor: "#ffffff",
    borderTop: "1px solid #e0e0e0",
    padding: theme.spacing(2, 3),
  },
  section: {
    backgroundColor: "#ffffff",
    border: "1px solid #e6e8ee",
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: "#6b7280",
    marginBottom: 2,
    textTransform: "uppercase",
  },
  value: {
    color: "#202124",
    fontWeight: 500,
  },
  tipoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  tipoButton: {
    border: "1px solid #d7dce5",
    borderRadius: 8,
    padding: theme.spacing(1.25),
    textAlign: "left",
    textTransform: "none",
    justifyContent: "flex-start",
    backgroundColor: "#ffffff",
  },
  tipoButtonActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: "#eef3ff",
    color: theme.palette.primary.main,
  },
  typeDescription: {
    display: "block",
    color: "#6b7280",
    fontSize: 11,
    lineHeight: 1.3,
    marginTop: 2,
  },
}));

export default function RectificadoModal(props) {
  const classes = useStyles();
  const { open, onClose, estudianteData } = props;

  const [tipoRectificacion, setTipoRectificacion] = useState("BAJA");
  const [observacion, setObservacion] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  useEffect(() => {
    if (open && estudianteData) {
      const tipoGuardado = estudianteData.tipoRectificacion;
      const tipoInicial = tipoGuardado
        ? String(tipoGuardado).toUpperCase()
        : estudianteData.rectificado && !estudianteData.fechaDesdeRectificado && !estudianteData.fechaHastaRectificado
          ? "OTROS"
          : "BAJA";

      setTipoRectificacion(["ALTA", "BAJA", "OTROS"].includes(tipoInicial) ? tipoInicial : "BAJA");
      setObservacion(estudianteData.observacionRectificado || "");
      setFechaDesde(parseDateOnly(estudianteData.fechaDesdeRectificado));
      setFechaHasta(parseDateOnly(estudianteData.fechaHastaRectificado));
    }
  }, [open, estudianteData]);

  useEffect(() => {
    if (tipoRectificacion === "OTROS") {
      setFechaDesde("");
      setFechaHasta("");
    }
  }, [tipoRectificacion]);

  const requiereFechas = tipoRectificacion === "ALTA" || tipoRectificacion === "BAJA";

  const getNombreEstudiante = () =>
    `${estudianteData?.convenioEstudiante?.estudiante?.nombres || ""} ${
      estudianteData?.convenioEstudiante?.estudiante?.apellidos || ""
    }`.trim() || "N/A";

  const getNombreTutor = () =>
    `${estudianteData?.convenioTutor?.tutor?.nombres || ""} ${
      estudianteData?.convenioTutor?.tutor?.apellidos || ""
    }`.trim() || "N/A";

  const validar = () => {
    if (!tipoRectificacion) {
      Alert.show({ message: "Seleccione el tipo de rectificacion", type: "error" });
      return false;
    }

    if (!observacion.trim()) {
      Alert.show({ message: "La observacion es obligatoria", type: "error" });
      return false;
    }

    if (requiereFechas && !fechaDesde) {
      Alert.show({ message: "Seleccione la fecha desde", type: "error" });
      return false;
    }

    if (requiereFechas && !fechaHasta) {
      Alert.show({ message: "Seleccione la fecha hasta", type: "error" });
      return false;
    }

    if (requiereFechas && fechaHasta < fechaDesde) {
      Alert.show({ message: "La fecha hasta debe ser posterior o igual a la fecha desde", type: "error" });
      return false;
    }

    return true;
  };

  const handleGuardar = async () => {
    if (!estudianteData || !validar()) {
      return;
    }

    const payload = {
      tipoRectificacion,
      observacionRectificado: observacion.trim(),
      fechaDesdeRectificado: requiereFechas && fechaDesde ? fechaDesde : null,
      fechaHastaRectificado: requiereFechas && fechaHasta ? fechaHasta : null,
    };

    try {
      await trackPromise(
        DnerhsApi.patch(`/practicas-detalles/${estudianteData.id}/rectificacion`, payload)
      );

      Alert.show({
        message: "Rectificacion guardada exitosamente",
        type: "success",
      });

      onClose(true);
    } catch (error) {
      console.log(error);
      if (error.response) {
        Alert.showServerError(error);
      }
    }
  };

  const handleCancelar = () => {
    onClose(false);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleCancelar}
      classes={{ paper: classes.drawerPaper }}
    >
      <Box className={classes.header} display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h6">Rectificar estudiante</Typography>
          <Typography variant="body2" color="textSecondary">
            La rectificacion quedara marcada al guardar.
          </Typography>
        </Box>
        <IconButton aria-label="cerrar" onClick={handleCancelar}>
          <CloseIcon />
        </IconButton>
      </Box>

      {estudianteData && (
        <Box className={classes.content}>
          <Box className={classes.section}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box>
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                  {getNombreEstudiante()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  CI {estudianteData.convenioEstudiante?.estudiante?.cedulaIdentidad || "N/A"}
                </Typography>
              </Box>
              <Chip
                size="small"
                icon={<AssignmentIcon />}
                label="Rectificacion"
                color="primary"
                variant="outlined"
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography className={classes.label}>Anio</Typography>
                <Typography className={classes.value}>{estudianteData.convenioEstudiante?.anio || "N/A"}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography className={classes.label}>Total horas</Typography>
                <Typography className={classes.value}>
                  {estudianteData.totalHoras ? `${estudianteData.totalHoras} hs` : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography className={classes.label}>Tutor</Typography>
                <Typography className={classes.value}>{getNombreTutor()}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography className={classes.label}>Establecimiento</Typography>
                <Typography className={classes.value}>
                  {estudianteData.establecimiento?.nombreServicio?.nombre || "N/A"}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Box className={classes.section}>
            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
              Tipo de rectificacion
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
              Seleccione el motivo operativo de la rectificacion.
            </Typography>

            <Box className={classes.tipoGrid}>
              {TIPOS_RECTIFICACION.map((tipo) => (
                <Button
                  key={tipo.value}
                  className={`${classes.tipoButton} ${
                    tipoRectificacion === tipo.value ? classes.tipoButtonActive : ""
                  }`}
                  onClick={() => setTipoRectificacion(tipo.value)}
                >
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 700 }}>
                      {tipo.label}
                    </Typography>
                    <span className={classes.typeDescription}>{tipo.description}</span>
                  </Box>
                </Button>
              ))}
            </Box>
          </Box>

          {requiereFechas && (
            <Box className={classes.section}>
              <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                Periodo
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                Para alta o baja debe indicar fecha desde y fecha hasta.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Fecha desde *"
                    type="date"
                    value={fechaDesde}
                    onChange={(event) => setFechaDesde(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={!fechaDesde}
                    helperText={!fechaDesde ? "Campo obligatorio" : ""}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Fecha hasta *"
                    type="date"
                    value={fechaHasta}
                    onChange={(event) => setFechaHasta(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: fechaDesde || undefined }}
                    error={!fechaHasta || (fechaDesde && fechaHasta && fechaHasta < fechaDesde)}
                    helperText={
                      !fechaHasta
                        ? "Campo obligatorio"
                        : fechaDesde && fechaHasta && fechaHasta < fechaDesde
                        ? "Debe ser posterior o igual a fecha desde"
                        : ""
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          <Box className={classes.section}>
            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
              Observacion
            </Typography>
            <Divider style={{ margin: "8px 0 16px" }} />
            <TextField
              label="Observacion *"
              multiline
              rows={5}
              fullWidth
              variant="outlined"
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              placeholder="Detalle el motivo de la rectificacion"
              required
              error={!observacion.trim()}
              helperText={!observacion.trim() ? "La observacion es obligatoria" : ""}
            />
          </Box>
        </Box>
      )}

      <Box className={classes.footer} display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
        <Button onClick={handleCancelar} color="default">
          Cancelar
        </Button>
        <Button onClick={handleGuardar} color="primary" variant="contained">
          Guardar rectificacion
        </Button>
      </Box>
    </Drawer>
  );
}
